// src/components/SurveyPublicPage.jsx
import React, { useState, useEffect } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Form,
  Spinner,
  Alert,
  ProgressBar,
  Modal,
  Badge
} from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import { surveysAPI } from '../services/api';

const SurveyPublicPage = () => {
  const { public_id } = useParams();
  const navigate = useNavigate();
  
  const [survey, setSurvey] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorModalMessage, setErrorModalMessage] = useState('');
  const [submissionResult, setSubmissionResult] = useState(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [transitionDirection, setTransitionDirection] = useState('next');
  
  const [respondentInfo, setRespondentInfo] = useState({
    email: '',
    name: ''
  });
  
  const [answers, setAnswers] = useState({});
  const [startTime, setStartTime] = useState(null);
  
  // Загрузка анкеты
  useEffect(() => {
    const loadSurvey = async () => {
      try {
        if (!public_id) {
          setError('Неверная ссылка на анкету');
          setLoading(false);
          return;
        }
        
        setLoading(true);
        setError('');
        
        const surveyData = await surveysAPI.getPublicSurvey(public_id);
        
        if (!surveyData) {
          throw new Error('Анкета не найдена');
        }
        
        const now = new Date();
        const startsAt = surveyData.starts_at ? new Date(surveyData.starts_at) : null;
        const endsAt = surveyData.ends_at ? new Date(surveyData.ends_at) : null;
        
        if ((surveyData.status && surveyData.status !== 'active') || 
            (surveyData.is_active !== undefined && !surveyData.is_active)) {
          throw new Error('Эта анкета неактивна');
        }
        
        if (startsAt && now < startsAt) {
          throw new Error('Эта анкета еще не началась');
        }
        
        if (endsAt && now > endsAt) {
          throw new Error('Эта анкета уже завершена');
        }
        
        setSurvey(surveyData);
        
        const initialAnswers = {};
        if (surveyData.questions && surveyData.questions.length > 0) {
          surveyData.questions.forEach(question => {
            if (question.question_type === 'multiple_choice') {
              initialAnswers[question.id] = [];
            } else if (question.question_type === 'rating') {
              initialAnswers[question.id] = null;
            } else if (question.question_type === 'single_choice') {
              initialAnswers[question.id] = '';
            } else if (question.question_type === 'yes_no') {
              initialAnswers[question.id] = null;
            } else {
              initialAnswers[question.id] = '';
            }
          });
        }
        setAnswers(initialAnswers);
        
        setStartTime(new Date());
        setLoading(false);
        
      } catch (err) {
        let errorMessage = 'Анкета не найдена или недоступна';
        
        if (err.response?.status === 404) {
          errorMessage = 'Анкета не найдена';
        } else if (err.response?.status === 403) {
          errorMessage = 'Доступ к анкете запрещен';
        } else if (err.response?.data?.detail) {
          errorMessage = err.response.data.detail;
        } else if (err.message) {
          errorMessage = err.message;
        }
        
        setError(errorMessage);
        setLoading(false);
      }
    };
    
    if (public_id) {
      loadSurvey();
    }
  }, [public_id]);
  
  const handleRespondentInfoChange = (field, value) => {
    setRespondentInfo(prev => ({
      ...prev,
      [field]: value
    }));
  };
  
  const handleAnswerChange = (questionId, value) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: value
    }));
  };
  
  const handleMultipleChoiceChange = (questionId, option) => {
    setAnswers(prev => {
      const currentAnswers = prev[questionId] || [];
      const updatedAnswers = currentAnswers.includes(option)
        ? currentAnswers.filter(item => item !== option)
        : [...currentAnswers, option];
      
      return {
        ...prev,
        [questionId]: updatedAnswers
      };
    });
  };
  
  const goToNextQuestion = () => {
    if (!survey || !survey.questions) return;
    
    const currentQuestion = getCurrentQuestion();
    
    if (currentQuestion && currentQuestion.is_required) {
      const answer = answers[currentQuestion.id];
      
      if (currentQuestion.question_type === 'multiple_choice') {
        if (!answer || answer.length === 0) {
          setError(`Пожалуйста, ответьте на вопрос: "${currentQuestion.question_text}"`);
          return;
        }
      } else if (!answer || (answer.toString().trim() === '' && answer !== false && answer !== null)) {
        if (!(currentQuestion.question_type === 'yes_no' && answer === false)) {
          setError(`Пожалуйста, ответьте на вопрос: "${currentQuestion.question_text}"`);
          return;
        }
      }
    }
    
    setError('');
    setTransitionDirection('next');
    
    if (currentQuestionIndex < survey.questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };
  
  const goToPreviousQuestion = () => {
    if (currentQuestionIndex > 0) {
      setTransitionDirection('prev');
      setCurrentQuestionIndex(currentQuestionIndex - 1);
      setError('');
    }
  };
  
  const getCurrentQuestion = () => {
    if (!survey || !survey.questions || survey.questions.length === 0) {
      return null;
    }
    
    const sortedQuestions = [...survey.questions].sort((a, b) => a.order_index - b.order_index);
    return sortedQuestions[currentQuestionIndex] || null;
  };
  
  const validateFormForSubmission = () => {
    if (!survey) return false;
    
    if (survey.require_email && !respondentInfo.email) {
      setError('Пожалуйста, укажите email');
      return false;
    }
    
    if (survey.require_email && respondentInfo.email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(respondentInfo.email)) {
        setError('Пожалуйста, введите корректный email');
        return false;
      }
    }
    
    if (!survey.questions) return true;
    
    const sortedQuestions = [...survey.questions].sort((a, b) => a.order_index - b.order_index);
    
    for (const question of sortedQuestions) {
      if (question.is_required) {
        const answer = answers[question.id];
        
        if (question.question_type === 'multiple_choice') {
          if (!answer || answer.length === 0) {
            setError(`Пожалуйста, ответьте на вопрос: "${question.question_text}"`);
            return false;
          }
        } else if (question.question_type === 'yes_no') {
          if (answer === null || answer === undefined) {
            setError(`Пожалуйста, ответьте на вопрос: "${question.question_text}"`);
            return false;
          }
        } else if (!answer || answer.toString().trim() === '') {
          setError(`Пожалуйста, ответьте на вопрос: "${question.question_text}"`);
          return false;
        }
        
        if (question.question_type === 'text' && answer) {
          const text = answer.toString().trim();
          if (question.min_length && text.length < question.min_length) {
            setError(`Ответ на вопрос "${question.question_text}" должен содержать минимум ${question.min_length} символов`);
            return false;
          }
          if (question.max_length && text.length > question.max_length) {
            setError(`Ответ на вопрос "${question.question_text}" должен содержать максимум ${question.max_length} символов`);
            return false;
          }
        }
      }
    }
    
    return true;
  };
  
  const handleSubmit = async () => {
    try {
      if (!validateFormForSubmission()) return;
      
      setSubmitting(true);
      setError('');
      
      const endTime = new Date();
      const completionTimeSeconds = startTime 
        ? Math.round((endTime - startTime) / 1000)
        : null;
      
      const submissionData = {
        respondent_email: survey.require_email ? respondentInfo.email : null,
        respondent_name: (!survey.is_anonymous && respondentInfo.name) ? respondentInfo.name : null,
        completion_time_seconds: completionTimeSeconds,
        answers: []
      };
      
      if (survey.questions) {
        const sortedQuestions = [...survey.questions].sort((a, b) => a.order_index - b.order_index);
        
        for (const question of sortedQuestions) {
          const answer = answers[question.id];
          
          if (answer === undefined || answer === null || answer === '' || 
              (Array.isArray(answer) && answer.length === 0)) {
            continue;
          }
          
          let answerObject = { question_id: question.id };
          
          switch (question.question_type) {
            case 'text':
              answerObject.text_answer = answer.toString().trim();
              break;
              
            case 'single_choice':
              answerObject.selected_option = answer.toString();
              break;
              
            case 'multiple_choice':
              answerObject.selected_options = answer;
              break;
              
            case 'rating':
              answerObject.rating_value = parseInt(answer);
              break;
              
            case 'yes_no':
              answerObject.selected_option = answer ? 'Да' : 'Нет';
              break;
              
            default:
              answerObject.text_answer = answer.toString();
          }
          
          submissionData.answers.push(answerObject);
        }
      }
      
      console.log('📤 Submitting survey:', submissionData);
      
      const result = await surveysAPI.submitPublicSurvey(public_id, submissionData);
      setSubmissionResult(result);
      setShowSuccessModal(true);
      setSubmitting(false);
      
    } catch (err) {
      console.error('❌ Error submitting survey:', err);
      
      let errorMessage = 'Ошибка при отправке анкеты';
      let isDuplicate = false;
      
      if (err.response?.status === 422) {
        const errorDetail = err.response?.data?.detail;
        if (Array.isArray(errorDetail)) {
          errorMessage = errorDetail.map(e => e.msg).join('; ');
        } else {
          errorMessage = 'Пожалуйста, проверьте правильность заполнения всех полей';
        }
      } 
      else if (err.response?.status === 400) {
        const errorDetail = err.response?.data?.detail;
        if (errorDetail && (
          errorDetail.includes('уже заполняли') || 
          errorDetail.includes('already submitted') ||
          errorDetail.includes('Only one submission')
        )) {
          errorMessage = '❌ Вы уже заполняли эту анкету. Повторная отправка невозможна.';
          isDuplicate = true;
        } else {
          errorMessage = errorDetail || 'Ошибка при отправке анкеты';
        }
      }
      else if (err.response?.status === 403) {
        errorMessage = '❌ Вы уже заполняли эту анкету. Повторная отправка невозможна.';
        isDuplicate = true;
      } 
      else if (err.response?.data?.detail) {
        errorMessage = err.response.data.detail;
      }
      
      if (isDuplicate) {
        setErrorModalMessage(errorMessage);
        setShowErrorModal(true);
      } else {
        setError(errorMessage);
      }
      
      setSubmitting(false);
    }
  };
  
  const calculateProgress = () => {
    if (!survey || !survey.questions) return 0;
    
    const totalQuestions = survey.questions.length;
    const answeredQuestions = survey.questions.filter(question => {
      const answer = answers[question.id];
      
      if (answer === undefined || answer === null) return false;
      
      if (question.question_type === 'multiple_choice') {
        return Array.isArray(answer) && answer.length > 0;
      } else if (question.question_type === 'yes_no') {
        return answer === true || answer === false;
      } else if (question.question_type === 'rating') {
        return answer !== undefined && answer !== null;
      } else if (question.question_type === 'single_choice') {
        return typeof answer === 'string' && answer.trim() !== '';
      } else {
        return typeof answer === 'string' && answer.trim() !== '';
      }
    }).length;
    
    return Math.round((answeredQuestions / totalQuestions) * 100);
  };
  
  const StarRating = ({ question, value, onChange }) => {
    const maxRating = question.max_value || 5;
    const minRating = question.min_value || 1;
    const totalStars = maxRating - minRating + 1;
    
    const [hoverRating, setHoverRating] = useState(0);
    const currentValue = value || minRating;
    
    return (
      <div className="star-rating">
        <div className="d-flex justify-content-center mb-2">
          {Array.from({ length: totalStars }, (_, index) => {
            const starValue = minRating + index;
            const isFilled = starValue <= (hoverRating || currentValue);
            
            return (
              <button
                key={index}
                type="button"
                className="btn btn-link p-1 border-0 bg-transparent"
                onClick={() => onChange(question.id, starValue)}
                onMouseEnter={() => setHoverRating(starValue)}
                onMouseLeave={() => setHoverRating(0)}
                style={{ 
                  fontSize: '2rem',
                  transition: 'transform 0.2s ease, color 0.3s ease'
                }}
              >
                <i
                  className={isFilled ? 'bi bi-star-fill text-warning' : 'bi bi-star text-muted'}
                  style={{
                    transition: 'transform 0.2s ease',
                    transform: hoverRating >= starValue ? 'scale(1.1)' : 'scale(1)'
                  }}
                />
              </button>
            );
          })}
        </div>
        <div className="text-center mt-2">
          <div className="d-flex justify-content-center align-items-center">
            <span className="me-2 fw-semibold text-muted">Оценка:</span>
            <span className="fs-5 fw-bold text-dark">{currentValue}</span>
            <span className="ms-1 text-muted">из {maxRating}</span>
          </div>
          <div className="text-muted small mt-1">
            {value === null && "Нажмите на звезду для выбора оценки"}
          </div>
        </div>
      </div>
    );
  };
  
  const renderCurrentQuestion = () => {
    const question = getCurrentQuestion();
    if (!question) return null;
    
    const answer = answers[question.id];
    
    const getQuestionTypeLabel = () => {
      switch(question.question_type) {
        case 'single_choice': return 'Одиночный выбор';
        case 'multiple_choice': return 'Множественный выбор';
        case 'text': return 'Текст';
        case 'rating': return 'Рейтинг';
        case 'yes_no': return 'Да/Нет';
        default: return question.question_type;
      }
    };
    
    return (
      <div 
        className="mb-4"
        style={{
          animation: transitionDirection === 'next' 
            ? 'slideInRight 0.3s ease-out' 
            : 'slideInLeft 0.3s ease-out'
        }}
      >
        <style>
          {`
            @keyframes slideInRight {
              from {
                opacity: 0;
                transform: translateX(30px);
              }
              to {
                opacity: 1;
                transform: translateX(0);
              }
            }
            
            @keyframes slideInLeft {
              from {
                opacity: 0;
                transform: translateX(-30px);
              }
              to {
                opacity: 1;
                transform: translateX(0);
              }
            }
          `}
        </style>
        
        <div className="d-flex justify-content-between align-items-start mb-3">
          <div>
            <span className="text-muted small">Вопрос {currentQuestionIndex + 1} из {survey?.questions?.length || 0}</span>
            <h5 className="mb-2 mt-1">
              {question.question_text}
              {question.is_required && (
                <span className="text-danger ms-1">*</span>
              )}
            </h5>
          </div>
          <Badge bg="light" text="dark" className="border">
            {getQuestionTypeLabel()}
          </Badge>
        </div>
        
        {question.description && (
          <div className="mb-4 p-3 bg-light rounded">
            <span className="text-muted">{question.description}</span>
          </div>
        )}
        
        {question.question_type === 'text' && (
          <Form.Group className="mb-3">
            <Form.Control
              as="textarea"
              rows={4}
              placeholder="Введите ваш ответ..."
              value={answer || ''}
              onChange={(e) => handleAnswerChange(question.id, e.target.value)}
              className="border-0 border-bottom rounded-0 px-0"
            />
            {question.min_length && question.max_length && (
              <div className="text-muted small mt-2">
                От {question.min_length} до {question.max_length} символов
              </div>
            )}
          </Form.Group>
        )}
        
        {question.question_type === 'single_choice' && question.options && (
          <div className="mb-3">
            {question.options.map((option, optIndex) => (
              <div key={optIndex} className="mb-2">
                <Form.Check
                  type="radio"
                  id={`q${question.id}-opt${optIndex}`}
                  name={`question-${question.id}`}
                  label={option}
                  checked={answer === option}
                  onChange={() => handleAnswerChange(question.id, option)}
                  className="fs-6"
                />
              </div>
            ))}
          </div>
        )}
        
        {question.question_type === 'multiple_choice' && question.options && (
          <div className="mb-3">
            {question.options.map((option, optIndex) => (
              <div key={optIndex} className="mb-2">
                <Form.Check
                  type="checkbox"
                  id={`q${question.id}-opt${optIndex}`}
                  label={option}
                  checked={(answer || []).includes(option)}
                  onChange={() => handleMultipleChoiceChange(question.id, option)}
                  className="fs-6"
                />
              </div>
            ))}
          </div>
        )}
        
        {question.question_type === 'yes_no' && (
          <div className="mb-3">
            <div className="d-flex gap-4">
              <Form.Check
                type="radio"
                id={`q${question.id}-yes`}
                name={`question-${question.id}`}
                label="Да"
                checked={answer === true}
                onChange={() => handleAnswerChange(question.id, true)}
                className="fs-6"
              />
              <Form.Check
                type="radio"
                id={`q${question.id}-no`}
                name={`question-${question.id}`}
                label="Нет"
                checked={answer === false}
                onChange={() => handleAnswerChange(question.id, false)}
                className="fs-6"
              />
              <Form.Check
                type="radio"
                id={`q${question.id}-not-sure`}
                name={`question-${question.id}`}
                label="Пропустить"
                checked={answer === null || answer === undefined}
                onChange={() => handleAnswerChange(question.id, null)}
                className="fs-6"
              />
            </div>
          </div>
        )}
        
        {question.question_type === 'rating' && (
          <StarRating 
            question={question} 
            value={answer}
            onChange={handleAnswerChange}
          />
        )}
      </div>
    );
  };
  
  if (!public_id) {
    return (
      <Container className="py-5">
        <Row className="justify-content-center">
          <Col md={8} className="text-center">
            <div className="mb-4">
              <div className="text-muted mb-3">Неверная ссылка на анкету</div>
              <Button 
                variant="outline-dark" 
                onClick={() => navigate('/')}
                className="px-4"
              >
                На главную
              </Button>
            </div>
          </Col>
        </Row>
      </Container>
    );
  }
  
  if (loading) {
    return (
      <Container className="py-5">
        <Row className="justify-content-center">
          <Col md={8} className="text-center">
            <Spinner animation="border" variant="secondary" className="mb-3" />
            <div className="text-muted">Загрузка анкеты...</div>
          </Col>
        </Row>
      </Container>
    );
  }
  
  if (error && !survey) {
    return (
      <Container className="py-5">
        <Row className="justify-content-center">
          <Col md={8} className="text-center">
            <div className="text-muted mb-3">{error}</div>
            <Button 
              variant="outline-dark" 
              onClick={() => navigate('/')}
              className="px-4"
            >
              На главную
            </Button>
          </Col>
        </Row>
      </Container>
    );
  }
  
  if (!survey) {
    return (
      <Container className="py-5">
        <Row className="justify-content-center">
          <Col md={8} className="text-center">
            <div className="text-muted mb-3">Анкета не найдена</div>
            <Button 
              variant="outline-dark" 
              onClick={() => navigate('/')}
              className="px-4"
            >
              На главную
            </Button>
          </Col>
        </Row>
      </Container>
    );
  }
  
  const sortedQuestions = survey.questions ? [...survey.questions].sort((a, b) => a.order_index - b.order_index) : [];
  const isLastQuestion = currentQuestionIndex === sortedQuestions.length - 1;
  const isFirstQuestion = currentQuestionIndex === 0;
  
  return (
    <Container className="py-4" style={{ maxWidth: '800px' }}>
      <style>
        {`
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
        `}
      </style>
      
      <div className="mb-5 text-center" style={{ animation: 'fadeIn 0.5s ease' }}>
        <h1 className="mb-3 fw-normal">{survey.title}</h1>
        {survey.description && (
          <p className="text-muted mb-4">{survey.description}</p>
        )}
        
        {survey.organization_name && (
          <div className="mb-3">
            <span className="text-muted">Медицинская организация:</span> <strong>{survey.organization_name}</strong>
          </div>
        )}
        
        <div className="d-flex justify-content-center gap-2">
          <Badge bg="transparent" text="dark" className="border">
            {survey.questions?.length || 0} вопросов
          </Badge>
          <Badge bg="transparent" text="dark" className="border">
            {survey.is_anonymous ? 'Анонимная' : 'Именная'}
          </Badge>
          {!survey.allow_multiple_submissions && (
            <Badge bg="warning" text="dark" className="border">
              <i className="bi bi-info-circle me-1"></i>
              Только одна отправка
            </Badge>
          )}
        </div>
      </div>
      
      {survey.questions && survey.questions.length > 0 && (
        <div className="mb-4">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <span className="text-muted">Прогресс</span>
            <span className="fw-semibold">{calculateProgress()}%</span>
          </div>
          <ProgressBar 
            now={calculateProgress()} 
            variant="primary"
            style={{ height: '8px' }}
          />
        </div>
      )}
      
      {(survey.require_email || !survey.is_anonymous) && currentQuestionIndex === 0 && (
        <div className="mb-5 p-4 bg-light rounded" style={{ animation: 'fadeIn 0.5s ease' }}>
          <h6 className="mb-3">Информация о респонденте</h6>
          <Row>
            {!survey.is_anonymous && (
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="text-muted small mb-1">Ваше имя</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Введите ваше имя"
                    value={respondentInfo.name}
                    onChange={(e) => handleRespondentInfoChange('name', e.target.value)}
                    className="border-0 border-bottom rounded-0 px-0 bg-transparent"
                  />
                </Form.Group>
              </Col>
            )}
            
            {survey.require_email && (
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="text-muted small mb-1">
                    Email {!survey.require_email && '(необязательно)'}
                  </Form.Label>
                  <Form.Control
                    type="email"
                    placeholder="email@example.com"
                    value={respondentInfo.email}
                    onChange={(e) => handleRespondentInfoChange('email', e.target.value)}
                    className="border-0 border-bottom rounded-0 px-0 bg-transparent"
                    required={survey.require_email}
                  />
                </Form.Group>
              </Col>
            )}
          </Row>
        </div>
      )}
      
      <div className="mb-4 d-flex justify-content-between align-items-center pt-4 border-top">
        <div>
          {!isFirstQuestion && (
            <Button
              variant="outline-primary"
              onClick={goToPreviousQuestion}
              className="px-4"
            >
              Назад
            </Button>
          )}
        </div>
        
        <div className="d-flex gap-3">
          <Button
            variant="outline-secondary"
            onClick={() => {
              if (window.confirm('Вы уверены? Все введенные данные будут потеряны.')) {
                navigate('/');
              }
            }}
            className="px-4"
          >
            Отмена
          </Button>
          
          {!isLastQuestion ? (
            <Button
              variant="primary"
              onClick={goToNextQuestion}
              className="px-4"
            >
              Далее
            </Button>
          ) : (
            <Button
              variant="primary"
              onClick={handleSubmit}
              disabled={submitting}
              className="px-4"
            >
              {submitting ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Отправка...
                </>
              ) : (
                'Отправить'
              )}
            </Button>
          )}
        </div>
      </div>
      
      {error && (
        <div className="mb-4 p-3 bg-light text-muted rounded">
          <div className="d-flex align-items-center">
            <i className="bi bi-exclamation-circle me-2 text-danger"></i>
            {error}
          </div>
        </div>
      )}
      
      <div className="mb-5">
        {survey.questions && survey.questions.length > 0 ? (
          renderCurrentQuestion()
        ) : (
          <div className="text-center text-muted py-4">
            В этой анкете пока нет вопросов
          </div>
        )}
      </div>
      
      <Modal show={showSuccessModal} onHide={() => navigate('/')} centered>
        <Modal.Header className="border-0">
          <Modal.Title className="w-100 text-center">
            <div className="text-success mb-2">
              <i className="bi bi-check2-circle" style={{ fontSize: '3rem' }}></i>
            </div>
            <h5 className="mb-0">Анкета успешно отправлена!</h5>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center py-4">
          <p className="text-muted mb-4">
            Спасибо за участие в опросе!
          </p>
          
          {submissionResult && (
            <div className="mt-3">
              <div className="text-muted small mb-2">Код отправки:</div>
              <div className="mb-3">
                <code className="fs-6 bg-light p-2 rounded">{submissionResult.submission_code}</code>
              </div>
              {submissionResult.completion_time_seconds && (
                <div className="text-muted small">
                  Время заполнения: {Math.round(submissionResult.completion_time_seconds / 60)} минут
                </div>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="border-0 justify-content-center">
          <Button 
            variant="primary" 
            onClick={() => navigate('/')}
            className="px-4"
          >
            Вернуться на главную
          </Button>
        </Modal.Footer>
      </Modal>
      
      <Modal show={showErrorModal} onHide={() => setShowErrorModal(false)} centered>
        <Modal.Header closeButton className="border-0">
          <Modal.Title className="w-100 text-center">
            <div className="text-warning mb-2">
              <i className="bi bi-exclamation-triangle" style={{ fontSize: '3rem' }}></i>
            </div>
            <h5 className="mb-0">Невозможно отправить анкету</h5>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center py-4">
          <p className="mb-4">
            {errorModalMessage || 'Вы уже заполняли эту анкету. Повторная отправка невозможна.'}
          </p>
          <Button 
            variant="primary" 
            onClick={() => {
              setShowErrorModal(false);
              navigate('/');
            }}
            className="px-4"
          >
            Вернуться на главную
          </Button>
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default SurveyPublicPage;